import type { MutableRefObject } from 'react'
import * as Yup from 'yup'
import { isValidNumber, getCountryCodeFromClient, getInternationalPhoneNumber } from '../../../pure-js/libs/PhoneHelper'
import { DanceRoles, Currency, Client } from '../../../pure-js/types/GrooverTypes'
import { getGrooverText } from '../libs/GrooverTextRepository'
import { isValidNorwegianFnr } from './NorwegianFnrHelper'

export const PhoneRegExp = /^\+?\d{1,3}\d{7,10}$/
export const EMAIL_REGEX = /\S+@\S+\.\S+/
export const SSN_REGEX = /^(?:\d{2})?\d{6}-?\d{4}$/

export const isValidEmail = (email: string) => {
  try {
    return EMAIL_REGEX.test(email)
  } catch (err) {
    console.log(err)
    return false
  }
}

// A plausible person name must contain at least two letters (any alphabet).
// Rejects nonsensical input like "-", "--", "123" or "!!" while still allowing
// hyphenated/apostrophed names such as "Anna-Lena" or "O'Brien".
// NOTE: currently only wired to the Step 2 soft-partner name input (by design);
// Step 1 name fields intentionally remain presence-only.
export const isValidPersonName = (name: string): boolean => {
  const letters = name.trim().match(/\p{L}/gu) ?? []
  return letters.length >= 2
}

export type CourseRegistrationFormValues = {
  name: string
  phoneNumber: string
  email: string
  role: DanceRoles | null
  partnerName: string
  partnerPhoneNumber: string
  partnerEmail: string
  partnerRole: DanceRoles | null
  mainUserSsn: string
  partnerUserSsn: string
  mainUserAddress: string
  partnerUserAddress: string
}

export const getValidationSchema = (
  client?: Client | null,
  mainUserPhoneCountryRef?: MutableRefObject<string | undefined>
) =>
  Yup.object().shape({
    name: Yup.string().required(getGrooverText('validationNameRequired')),
    phoneNumber: Yup.string()
      .required(getGrooverText('validationPhoneRequired'))
      .test('is-valid-phone', getGrooverText('validationPhoneInvalid'), (value) => {
        if (!value) return false
        const defaultCountry = (mainUserPhoneCountryRef?.current as any) ?? getCountryCodeFromClient(client)
        return isValidNumber(value, defaultCountry)
      }),
    email: Yup.string()
      .matches(EMAIL_REGEX, getGrooverText('validationEmailInvalid'))
      .required(getGrooverText('validationEmailRequired')),
    role: Yup.string().required()
  })

export const getValidationSchemaSsn = (
  currency?: Currency,
  client?: Client | null,
  mainUserPhoneCountryRef?: MutableRefObject<string | undefined>
) => {
  const clientCountry = getCountryCodeFromClient(client)
  const baseSchema = Yup.object().shape({
    name: Yup.string().required(getGrooverText('validationNameRequired')),
    phoneNumber: Yup.string()
      .required(getGrooverText('validationPhoneRequired'))
      .test('is-valid-phone', getGrooverText('validationPhoneInvalid'), (value) => {
        if (!value) return false
        const defaultCountry = (mainUserPhoneCountryRef?.current as any) ?? clientCountry
        return isValidNumber(value, defaultCountry)
      }),
    email: Yup.string()
      .matches(EMAIL_REGEX, getGrooverText('validationEmailInvalid'))
      .required(getGrooverText('validationEmailRequired')),
    role: Yup.string().required(),
    mainUserSsn: Yup.string()
      .transform((value) => (value === '' ? undefined : value))
      .notRequired()
      .nullable(),
    mainUserAddress: Yup.string()
      .transform((value) => (value === '' ? undefined : value))
      .notRequired()
      .nullable()
  })

  if (currency === 'NOK') {
    return baseSchema.shape({
      mainUserSsn: Yup.string()
        .transform((value) => (value === '' ? undefined : value))
        .notRequired()
        .test('is-valid-fnr', getGrooverText('validationFnrInvalidNorway'), (value) => {
          if (!value) return true
          return isValidNorwegianFnr(value)
        })
        .nullable()
    })
  }

  // EUR and other non-Scandinavian currencies: no SSN format requirement
  // Guard against undefined (still-loading) currency — fall through to SEK validation in that case
  if (currency && currency !== 'SEK') {
    return baseSchema
  }

  // Default: Swedish-style format
  return baseSchema.shape({
    mainUserSsn: Yup.string()
      .transform((value) => (value === '' ? undefined : value))
      .notRequired()
      .matches(SSN_REGEX, getGrooverText('validationSsnInvalidSweden'))
      .nullable()
  })
}

export const getValidationSchemaPartner = (
  client?: Client | null,
  mainUserPhoneCountryRef?: MutableRefObject<string | undefined>,
  partnerPhoneCountryRef?: MutableRefObject<string | undefined>
) =>
  Yup.object().shape({
    name: Yup.string().required(getGrooverText('validationNameRequired')),
    phoneNumber: Yup.string()
      .required(getGrooverText('validationPhoneRequired'))
      .test('is-valid-phone', getGrooverText('validationPhoneInvalid'), (value) => {
        if (!value) return false
        const defaultCountry = (mainUserPhoneCountryRef?.current as any) ?? getCountryCodeFromClient(client)
        return isValidNumber(value, defaultCountry)
      }),
    email: Yup.string()
      .matches(EMAIL_REGEX, getGrooverText('validationEmailInvalid'))
      .required(getGrooverText('validationEmailRequired')),
    role: Yup.string().required(),
    mainUserSsn: Yup.string(),
    mainUserAddress: Yup.string(),
    partnerName: Yup.string().required(getGrooverText('validationNameRequired')),
    partnerPhoneNumber: Yup.string()
      .required(getGrooverText('validationPhoneRequired'))
      .test('is-valid-phone', getGrooverText('validationPhoneInvalid'), (value) => {
        if (!value) return false
        const defaultCountry = (partnerPhoneCountryRef?.current as any) ?? getCountryCodeFromClient(client)
        return isValidNumber(value, defaultCountry)
      })
      .test('different-phone', getGrooverText('registrationValidationsPartnerPhoneDifferent'), function (value) {
        const mainPhone = this.parent.phoneNumber
        if (!value || !mainPhone) return true
        const mainCountry = (mainUserPhoneCountryRef?.current as any) ?? getCountryCodeFromClient(client)
        const partnerCountry = (partnerPhoneCountryRef?.current as any) ?? getCountryCodeFromClient(client)
        return (
          getInternationalPhoneNumber(value, partnerCountry) !== getInternationalPhoneNumber(mainPhone, mainCountry)
        )
      }),
    partnerEmail: Yup.string()
      .matches(EMAIL_REGEX, getGrooverText('validationEmailInvalid'))
      .required(getGrooverText('validationEmailRequired')),
    partnerRole: Yup.string().required()
  })

export const getValidationSchemaPartnerSsn = (
  currency?: Currency,
  client?: Client | null,
  mainUserPhoneCountryRef?: MutableRefObject<string | undefined>,
  partnerPhoneCountryRef?: MutableRefObject<string | undefined>
) => {
  const clientCountry = getCountryCodeFromClient(client)
  const baseSchema = Yup.object().shape({
    name: Yup.string().required(getGrooverText('validationNameRequired')),
    phoneNumber: Yup.string()
      .required(getGrooverText('validationPhoneRequired'))
      .test('is-valid-phone', getGrooverText('validationPhoneInvalid'), (value) => {
        if (!value) return false
        const mainCountry = (mainUserPhoneCountryRef?.current as any) ?? clientCountry
        return isValidNumber(value, mainCountry)
      }),
    email: Yup.string()
      .matches(EMAIL_REGEX, getGrooverText('validationEmailInvalid'))
      .required(getGrooverText('validationEmailRequired')),
    role: Yup.string().required(),
    mainUserSsn: Yup.string()
      .transform((value) => (value === '' ? undefined : value))
      .notRequired()
      .nullable(),
    mainUserAddress: Yup.string()
      .transform((value) => (value === '' ? undefined : value))
      .notRequired()
      .nullable(),
    partnerName: Yup.string().required(getGrooverText('validationNameRequired')),
    partnerPhoneNumber: Yup.string()
      .required(getGrooverText('validationPhoneRequired'))
      .test('is-valid-phone', getGrooverText('validationPhoneInvalid'), (value) => {
        if (!value) return false
        const partnerCountry = (partnerPhoneCountryRef?.current as any) ?? clientCountry
        return isValidNumber(value, partnerCountry)
      })
      .test('different-phone', getGrooverText('registrationValidationsPartnerPhoneDifferent'), function (value) {
        const mainPhone = this.parent.phoneNumber
        if (!value || !mainPhone) return true
        const mainCountry = (mainUserPhoneCountryRef?.current as any) ?? clientCountry
        const partnerCountry = (partnerPhoneCountryRef?.current as any) ?? clientCountry
        return (
          getInternationalPhoneNumber(value, partnerCountry) !== getInternationalPhoneNumber(mainPhone, mainCountry)
        )
      }),
    partnerEmail: Yup.string()
      .matches(EMAIL_REGEX, getGrooverText('validationEmailInvalid'))
      .required(getGrooverText('validationEmailRequired')),
    partnerRole: Yup.string().required(),
    partnerUserSsn: Yup.string()
      .transform((value) => (value === '' ? undefined : value))
      .notRequired()
      .nullable(),
    partnerUserAddress: Yup.string()
      .transform((value) => (value === '' ? undefined : value))
      .notRequired()
      .nullable()
  })

  if (currency === 'NOK') {
    return baseSchema.shape({
      mainUserSsn: Yup.string()
        .transform((value) => (value === '' ? undefined : value))
        .notRequired()
        .test('is-valid-fnr', getGrooverText('validationFnrInvalidNorway'), (value) => {
          if (!value) return true
          return isValidNorwegianFnr(value)
        })
        .nullable(),
      partnerUserSsn: Yup.string()
        .transform((value) => (value === '' ? undefined : value))
        .notRequired()
        .test('is-valid-fnr', getGrooverText('validationFnrInvalidNorway'), (value) => {
          if (!value) return true
          return isValidNorwegianFnr(value)
        })
        .nullable()
    })
  }

  // EUR and other non-Scandinavian currencies: no SSN format requirement
  // Guard against undefined (still-loading) currency — fall through to SEK validation in that case
  if (currency && currency !== 'SEK') {
    return baseSchema
  }

  // Default: Swedish-style format
  return baseSchema.shape({
    mainUserSsn: Yup.string()
      .transform((value) => (value === '' ? undefined : value))
      .notRequired()
      .matches(SSN_REGEX, getGrooverText('validationSsnInvalidSweden'))
      .nullable(),
    partnerUserSsn: Yup.string()
      .transform((value) => (value === '' ? undefined : value))
      .notRequired()
      .matches(SSN_REGEX, getGrooverText('validationSsnInvalidSweden'))
      .nullable()
  })
}
